import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { postsAPI } from '../api/posts';
import LazyPostCard from '../components/LazyPostCard';
import EditPostModal from '../components/EditPostModal';
import PostDetailModal from '../components/PostDetailModal';
import './FeedPage.css';

const FeedPage = () => {
  const observerTarget = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingPost, setEditingPost] = useState(null);
  const postRefs = useRef({});
  const [searchParams, setSearchParams] = useSearchParams();
  
  // 모달 상태 (URL 쿼리 파라미터로 관리)
  const modalPostId = searchParams.get('modal');
  const modalSlide = parseInt(searchParams.get('slide') || '0', 10);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery(
    'feed',
    ({ pageParam = 0 }) => postsAPI.getFeed(pageParam, 20),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.last) return undefined;
        return lastPage.pageable.pageNumber + 1;
      },
      refetchOnWindowFocus: false,
    }
  );

  // Intersection Observer를 이용한 무한스크롤
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  React.useEffect(() => {
    const element = observerTarget.current;
    const option = { threshold: 0.5 };
    const observer = new IntersectionObserver(handleObserver, option);

    if (element) observer.observe(element);
    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver]);

  const posts = data?.pages.flatMap((page) => page.content) || [];

  // 탐색에서 클릭한 포스트로 스크롤
  useEffect(() => {
    const scrollToPostId = location.state?.scrollToPostId;
    
    if (scrollToPostId && posts.length > 0) {
      // 잠시 대기 후 스크롤 (DOM 렌더링 완료 대기)
      setTimeout(() => {
        const targetElement = postRefs.current[scrollToPostId];
        if (targetElement) {
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length]);

  if (isLoading) {
    return <div className="feed-loading">로딩 중...</div>;
  }

  if (isError) {
    return <div className="feed-error">피드를 불러올 수 없습니다.</div>;
  }

  const handlePostClick = (post, slideIndex = 0) => {
    setSearchParams({ modal: post.postId.toString(), slide: slideIndex.toString() });
  };

  const handleCloseModal = () => {
    // 이전 페이지 정보가 있으면 해당 페이지로 이동 (탐색에서 왔으면 탐색으로)
    // replace: true로 히스토리 교체하여 뒤로가기 시 모달이 다시 열리지 않도록
    if (location.state?.from) {
      navigate(location.state.from, { replace: true });
    } else {
      // 피드에서 열었으면 그냥 파라미터만 제거
      setSearchParams({});
    }
  };

  const handleNextPost = () => {
    if (!modalPostId) return;
    const currentIndex = posts.findIndex(p => p.postId.toString() === modalPostId);
    if (currentIndex >= 0 && currentIndex < posts.length - 1) {
      const nextPost = posts[currentIndex + 1];
      // replace: true로 히스토리를 쌓지 않음 + state 유지 (탐색에서 왔으면 탐색으로 돌아가기 위해)
      navigate(`/feed?modal=${nextPost.postId}&slide=0`, { 
        replace: true, 
        state: location.state 
      });
    }
  };

  const handlePrevPost = () => {
    if (!modalPostId) return;
    const currentIndex = posts.findIndex(p => p.postId.toString() === modalPostId);
    if (currentIndex > 0) {
      const prevPost = posts[currentIndex - 1];
      // replace: true로 히스토리를 쌓지 않음 + state 유지 (탐색에서 왔으면 탐색으로 돌아가기 위해)
      navigate(`/feed?modal=${prevPost.postId}&slide=0`, { 
        replace: true, 
        state: location.state 
      });
    }
  };

  const handleSlideChange = (slideIndex) => {
    if (!modalPostId) return;
    // replace: true로 히스토리를 쌓지 않음 + state 유지 (탐색에서 왔으면 탐색으로 돌아가기 위해)
    navigate(`/feed?modal=${modalPostId}&slide=${slideIndex}`, { 
      replace: true, 
      state: location.state 
    });
  };

  const handleEdit = (post) => {
    setEditingPost(post);
  };

  const handleDelete = async (post) => {
    if (window.confirm('정말 이 게시물을 삭제하시겠습니까?')) {
      try {
        await postsAPI.deletePost(post.postId);
        // 캐시 무효화 및 리페치
        queryClient.invalidateQueries('feed');
        alert('게시물이 삭제되었습니다.');
      } catch (error) {
        console.error('게시물 삭제 실패:', error);
        alert('게시물 삭제에 실패했습니다.');
      }
    }
  };

  const handleSaveEdit = async (data) => {
    try {
      await postsAPI.updatePost(editingPost.postId, data);
      // 캐시 무효화 및 리페치
      queryClient.invalidateQueries('feed');
      setEditingPost(null);
      alert('게시물이 수정되었습니다.');
    } catch (error) {
      console.error('게시물 수정 실패:', error);
      throw error;
    }
  };

  if (posts.length === 0) {
    return (
      <div className="feed-empty">
        <p>아직 게시물이 없습니다.</p>
        <p>첫 번째 게시물을 업로드해보세요!</p>
      </div>
    );
  }

  return (
    <div className="feed-container">
      <div className="feed-list">
        {posts.map((post) => {
          return (
            <div 
              key={post.postId}
              ref={(el) => {
                if (el) postRefs.current[post.postId] = el;
              }}
            >
              <LazyPostCard 
                post={post} 
                onClick={handlePostClick}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </div>
          );
        })}
      </div>

      {/* 무한스크롤 트리거 */}
      <div ref={observerTarget} className="scroll-trigger" />

      {isFetchingNextPage && (
        <div className="feed-loading-more">더 불러오는 중...</div>
      )}

      {!hasNextPage && posts.length > 0 && (
        <div className="feed-end">모든 게시물을 확인했습니다.</div>
      )}

      {/* 수정 모달 */}
      {editingPost && (
        <EditPostModal 
          post={editingPost} 
          onClose={() => setEditingPost(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* 상세보기 모달 */}
      {modalPostId && (() => {
        const modalPost = posts.find(p => p.postId.toString() === modalPostId);
        const currentIndex = posts.findIndex(p => p.postId.toString() === modalPostId);
        const hasNext = currentIndex >= 0 && currentIndex < posts.length - 1;
        const hasPrev = currentIndex > 0;
        const prevPost = hasPrev ? posts[currentIndex - 1] : null;
        const nextPost = hasNext ? posts[currentIndex + 1] : null;

        return modalPost ? (
          <PostDetailModal
            post={modalPost}
            prevPost={prevPost}
            nextPost={nextPost}
            initialSlide={modalSlide}
            onClose={handleCloseModal}
            onNext={hasNext ? handleNextPost : null}
            onPrev={hasPrev ? handlePrevPost : null}
            onSlideChange={handleSlideChange}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ) : null;
      })()}
    </div>
  );
};

export default FeedPage;

